import { BriefcaseIcon, GraduationCapIcon, PresentationIcon, SparklesIcon, UserIcon, UsersIcon } from 'lucide-react'

export const PRICE = '$19'
export const BRAND = 'Makeup Instructor Ready'

export const systemFlow = ['Know', 'Teach', 'Plan', 'Practice', 'Assess', 'Manage']

/** Before and after. */
export const beforeSystem = [
  'Scattered notes',
  'Last-minute lesson planning',
  'Random exercises',
  'Searching online for ideas',
  'Creating student materials',
  'Unstructured feedback',
  'No clear student progression',
]

export const afterSystem = [
  'Structured lessons',
  'Technique teaching guides',
  'Student practice activities',
  'Professional worksheets',
  'Skill assessments',
  'Progress tracking',
  'Classroom organization',
]

/** The six core volumes. */
export const volumes = [
  {
    number: '01',
    stage: 'Know',
    title: 'Makeup Education Foundations',
    benefit: 'Your professional reference for teaching the fundamentals behind makeup.',
    includes: [
      'Professional terminology',
      'Products & tools',
      'Skin preparation',
      'Foundation matching',
      'Undertones',
      'Color theory',
      'Facial analysis',
      'Eye shapes',
      'Brows & lips',
      'Hygiene principles',
    ],
    useWhen: 'You need to explain the WHY behind a technique.',
  },
  {
    number: '02',
    stage: 'Teach',
    title: 'Professional Makeup Techniques',
    benefit: 'A structured technique library for demonstrations and instruction.',
    includes: [
      'Complexion techniques',
      'Contouring',
      'Brows',
      'Eyeshadow',
      'Blending',
      'Eyeliner',
      'Lashes',
      'Lips',
      'Complete looks',
    ],
    useWhen: "You're preparing a technique demonstration.",
  },
  {
    number: '03',
    stage: 'Plan',
    title: 'Ready-to-Teach Lesson System',
    benefit: 'Turn your expertise into structured classes.',
    includes: [
      'Lesson frameworks',
      'Class structures',
      'Teaching sequences',
      'Demonstrations',
      'Guided practice',
      'Workshops',
      'Multi-session courses',
      'Instructor guidance',
    ],
    useWhen: "You don't want to build tomorrow's class from zero.",
  },
  {
    number: '04',
    stage: 'Practice',
    title: 'Student Practice & Skills Workbook',
    benefit: 'Give students structured activities instead of telling them to “just practice.”',
    includes: [
      'Face mapping',
      'Foundation matching',
      'Complexion labs',
      'Color theory exercises',
      'Brow drills',
      'Eyeshadow mapping',
      'Eyeliner practice',
      'Lip exercises',
      'Client briefs',
      'Troubleshooting',
      'Timed challenges',
      'Progress tracking',
    ],
    useWhen: 'Your students need deliberate hands-on practice.',
  },
  {
    number: '05',
    stage: 'Assess',
    title: 'Assessment & Feedback System',
    benefit: 'Know exactly where each student needs improvement.',
    includes: [
      'Diagnostic assessments',
      'Technique evaluations',
      'Skill rubrics',
      'Practical assessments',
      'Feedback frameworks',
      'Progress reviews',
      'Reassessment',
      'Final readiness',
    ],
    useWhen: 'You need to evaluate skills and give structured feedback.',
  },
  {
    number: '06',
    stage: 'Manage',
    title: 'Professional Instructor & Classroom System',
    benefit: 'Bring professional organization to your makeup education.',
    includes: [
      'Course planning',
      'Student records',
      'Attendance',
      'Classroom organization',
      'Inventory',
      'Hygiene systems',
      'Instructor workflows',
      'Course completion',
    ],
    useWhen: 'You want your classes to run like a professional education program.',
  },
]

/** Section 7 — real Volume 4 pages, in file-name order (01 → 06). */
export const galleryPages = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/mpdcw1G2maVrrwgFbRN68x/01-shadow-map-5-look-types.webp',
    title: 'Shadow Map Practice — 5 Look Types',
    verb: 'Plan it.',
    caption: 'Map techniques before application.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/gtVEXtP8gtk6rn49Qa46e3/02-troubleshooting-13-problems.webp',
    title: 'Troubleshooting Workout — 13 Problems to Diagnose',
    verb: 'Correct it.',
    caption: 'Teach students to identify and solve real makeup problems.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/o5VnSquF3UUN5nN2r1Ukjn/03-face-mapping-contour-bronzer-blush-highlight.webp',
    title: 'Contour, Bronzer, Blush & Highlight Mapping',
    verb: 'Practice it.',
    caption: 'Give students structured exercises.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/bf1VoJ8nzuZ7FujKJaFh1X/04-practice-log-evidence-progress.webp',
    title: 'Practice Log & Evidence of Progress',
    verb: 'Track it.',
    caption: 'Document practice, feedback and improvement.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/czXZmsoqoHoB5rb9gY2dpw/05-day-to-evening-transformation.webp',
    title: 'Look Planner — Day to Evening Transformation',
    verb: 'Challenge it.',
    caption: 'Client-style briefs that build real-world thinking.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/vRTpuvpzxW3bFCTJXpqwdN/06-final-readiness-audit-15-skills.webp',
    title: 'Final Readiness Audit — 15 Skill Areas',
    verb: 'Assess it.',
    caption: 'Know when students are ready to progress.',
  },
]

/** Section 8 — ways to teach with it. */
export const useCases = [
  { icon: UserIcon, title: '1:1 Makeup Lessons', description: 'Build structured private training.' },
  { icon: UsersIcon, title: 'Group Classes', description: 'Give every student clear objectives and exercises.' },
  { icon: GraduationCapIcon, title: 'Makeup Academies', description: 'Create greater consistency across your education.' },
  { icon: PresentationIcon, title: 'Workshops', description: 'Prepare focused learning experiences faster.' },
  { icon: SparklesIcon, title: 'Masterclasses', description: 'Support demonstrations with structured resources.' },
  {
    icon: BriefcaseIcon,
    title: 'New Makeup Educators',
    description: 'Stop staring at a blank page wondering how to organize what you know.',
  },
]

/** Section 9 — the hidden work behind one class. */
export const oldWorkflow = [
  'Choose Topic',
  'Research',
  'Structure Lesson',
  'Create Exercise',
  'Prepare Practice',
  'Create Assessment',
  'Organize Feedback',
]

export const newWorkflow = ['Open', 'Choose', 'Prepare', 'Teach']

/** Section 10 — the five extra bonuses. */
export const bonuses = [
  {
    number: 'Bonus #1',
    title: 'The Ultimate Makeup Face Chart Library',
    coverTitle: 'Face Chart Library',
    description: 'Visual face charts and mapping resources.',
    color: '#F6D3CF',
  },
  {
    number: 'Bonus #2',
    title: '50 Ready-to-Use Makeup Class Activities',
    coverTitle: '50 Class Activities',
    description: 'More ideas for practical, engaging classes.',
    color: '#F4DCC4',
  },
  {
    number: 'Bonus #3',
    title: 'The Makeup Instructor Quick Reference Guide',
    coverTitle: 'Quick Reference Guide',
    description: 'Fast-access professional teaching references.',
    color: '#D6E6D3',
  },
  {
    number: 'Bonus #4',
    title: '50 Ready-to-Teach Makeup Lesson Ideas',
    coverTitle: '50 Lesson Ideas',
    description: 'Never wonder what to teach next.',
    color: '#DED3F1',
  },
  {
    number: 'Bonus #5',
    title: 'The Professional Makeup Instructor Printable Pack',
    coverTitle: 'Printable Pack',
    description: 'Additional practical classroom resources.',
    color: '#F3E0B5',
  },
]

/** Section 11 — everything included. */
export const coreIncludes = [
  'Makeup Education Foundations',
  'Professional Makeup Techniques',
  'Ready-to-Teach Lesson System',
  'Student Practice & Skills Workbook',
  'Assessment & Feedback System',
  'Professional Instructor & Classroom System',
]

export const bonusIncludes = [
  'Face Chart Library',
  '50 Class Activities',
  'Quick Reference Guide',
  '50 Lesson Ideas',
  'Professional Printable Pack',
]

/** Section 13 — who it is for. */
export const audience = [
  'Makeup Instructors',
  'Makeup Educators',
  'Beauty School Teachers',
  'Makeup Academy Owners',
  'Independent Makeup Trainers',
  'Professional MUAs Who Teach',
  'Workshop Educators',
  'MUAs Starting to Teach',
]

export const faqs = [
  {
    question: 'Is this a makeup course?',
    answer:
      'No. Makeup Instructor Ready is a professional teaching resource system for makeup instructors, educators and MUAs who teach.',
  },
  {
    question: 'Will it help me prepare my classes?',
    answer:
      'Yes. The system includes structured teaching frameworks, technique resources, lesson structures, student exercises, assessments and instructor tools designed to reduce the amount of material you need to create from scratch.',
  },
  {
    question: 'Can I use it immediately?',
    answer:
      'Yes. Open the relevant volume, select the resource that fits your lesson and incorporate it into your class preparation.',
  },
  {
    question: 'Is it only for experienced instructors?',
    answer:
      'No. It can support established educators and professional MUAs who are beginning to structure their teaching.',
  },
  {
    question: 'Can I use it for private and group classes?',
    answer: 'Yes. The system can support 1:1 training, group classes, workshops and other makeup education formats.',
  },
  {
    question: 'Are the files editable?',
    answer: 'No. Resources are delivered as finished digital PDF materials.',
  },
  {
    question: 'What do I receive?',
    answer: 'Six core professional volumes plus five additional bonus resources.',
  },
  {
    question: 'How much does it cost?',
    answer: '$19 USD as a one-time payment.',
  },
]
